import { useEffect, useState } from "react";
import { api, ROLE_LABEL, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import { Plus, Trash2, Download, Search, Archive as ArchiveIcon } from "lucide-react";

const UPLOADER_ROLES = ["chief","deputy_chief","head_councillor","councillor","executive_director","band_registry_clerk"];
const CATEGORIES = [
  ["all", "All Records", "#FFFFFF"],
  ["treaty", "Treaty & Legal", "#d4b876"],
  ["historical", "Historical", "#89b4fa"],
  ["cultural", "Cultural", "#ef5b6b"],
  ["bcr", "Band Council Resolutions", "#8C8C8C"],
  ["media", "Media / Photos", "#FFFFFF"],
  ["other", "Other", "#8C8C8C"],
];

export default function Archive() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const canUpload = UPLOADER_ROLES.includes(user?.role) || user?.is_super_admin;
  const confirm = useConfirm();

  const load = () => {
    const params = {};
    if (cat !== "all") params.category = cat;
    if (q.trim()) params.q = q.trim();
    api.get("/archive", { params }).then(r=>setItems(r.data));
  };
  useEffect(() => { load(); }, [cat]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (id) => {
    const ok = await confirm({ title: "Remove from archive?", destructive: true, confirmLabel: "Remove" });
    if (!ok) return;
    try {
      await api.delete(`/archive/${id}`);
      toast.success("Record removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="flex justify-between items-baseline flex-wrap gap-3">
        <div>
          <div className="label-mono text-[#d4b876]">COMMUNITY ARCHIVE</div>
          <h1 className="font-display text-4xl mt-2">Historical & cultural records</h1>
          <p className="text-white/60 mt-2 max-w-2xl">Treaties, band council resolutions, cultural documents, photos, and elder recordings — preserved and searchable. Uploaded by leadership and the registry clerk.</p>
        </div>
        {canUpload && <button onClick={()=>setShowAdd(true)} className="btn-cta primary" data-testid="new-archive-btn"><Plus size={12}/> Add record</button>}
      </div>

      <div className="mt-10 flex flex-wrap gap-2">
        {CATEGORIES.map(([k, l, c]) => (
          <button key={k} onClick={()=>setCat(k)}
            className="px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest"
            style={cat===k ? {borderColor:c, color:c, background: `${c}12`} : {borderColor:"rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.6)"}}
            data-testid={`archive-cat-${k}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="mt-6 flex items-center gap-3 border border-white/10 px-3 py-2 max-w-md">
        <Search size={14} className="text-white/40"/>
        <input value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load()} placeholder="Search titles, descriptions, tags..." className="flex-1 !border-0 !bg-transparent px-0 py-0 font-mono text-xs focus:!shadow-none" style={{boxShadow:"none"}} data-testid="archive-search"/>
        {q && <button onClick={()=>{setQ(""); setTimeout(load, 0);}} className="font-mono text-[10px] text-white/40">clear</button>}
        <button onClick={load} className="font-mono text-[10px] uppercase tracking-widest text-[#89b4fa]" data-testid="archive-search-btn">Search</button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
        {items.length === 0 && (
          <div className="col-span-full border border-dashed border-white/15 p-10 text-center">
            <ArchiveIcon size={24} className="mx-auto text-white/30"/>
            <div className="font-mono text-xs text-white/40 uppercase tracking-widest mt-3">No records in this view</div>
            {canUpload && <button onClick={()=>setShowAdd(true)} className="btn-cta mt-4 mx-auto" data-testid="empty-new-archive">Add the first record</button>}
          </div>
        )}
        {items.map(it => {
          const catConf = CATEGORIES.find(c=>c[0]===it.category);
          const color = catConf ? catConf[2] : "#fff";
          return (
            <div key={it.id} className="panel p-5" data-testid={`archive-${it.id}`}>
              <div className="flex justify-between items-start gap-3">
                <span className="tag" style={{color}}>{catConf?catConf[1]:it.category}</span>
                {it.year && <span className="font-mono text-xs text-white/50">{it.year}</span>}
              </div>
              <div className="font-display text-lg mt-3">{it.title}</div>
              {it.description && <div className="text-white/60 text-sm mt-2 line-clamp-3">{it.description}</div>}
              {it.tags && it.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1">
                  {it.tags.map(t => <span key={t} className="font-mono text-[10px] px-1.5 py-0.5 border border-white/10 text-white/50">#{t}</span>)}
                </div>
              )}
              <div className="mt-4 flex justify-between items-center">
                {it.file_id ? (
                  <a href={`${API}/files/${it.file_id}`} target="_blank" rel="noreferrer" className="btn-cta text-xs" data-testid={`archive-file-${it.file_id}`}><Download size={12}/> Open</a>
                ) : <span className="font-mono text-[10px] text-white/30 uppercase tracking-widest">Reference-only</span>}
                {(it.added_by === user.id || user.role === "band_registry_clerk") && (
                  <button onClick={()=>del(it.id)} className="text-[#ef5b6b] hover:text-white" data-testid={`del-archive-${it.id}`}><Trash2 size={14}/></button>
                )}
              </div>
              <div className="font-mono text-[10px] text-white/30 mt-3 border-t border-white/10 pt-2">Added by {it.added_by_name} · {ROLE_LABEL[it.added_by_role]}</div>
            </div>
          );
        })}
      </div>

      {showAdd && <ArchiveForm onClose={()=>setShowAdd(false)} onSaved={load} />}
    </div>
  );
}

function ArchiveForm({ onClose, onSaved }) {
  const [f, setF] = useState({ title:"", category:"historical", year:"", description:"", tags:"" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setSaving(true); setErr("");
    try {
      let file_id = null;
      if (file) {
        const fd = new FormData(); fd.append("file", file);
        const res = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" }});
        file_id = res.data.id;
      }
      const payload = {
        title: f.title,
        category: f.category,
        year: f.year ? Number(f.year) : null,
        description: f.description || null,
        tags: f.tags.split(",").map(t=>t.trim().toLowerCase()).filter(Boolean),
        file_id,
      };
      await api.post("/archive", payload);
      toast.success("Record added to archive");
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={e=>e.stopPropagation()} onSubmit={submit} className="w-full max-w-lg border border-white/20 bg-[#181825] p-6 space-y-4 my-8">
        <div className="label-mono text-[#d4b876]">ADD TO COMMUNITY ARCHIVE</div>
        <input required placeholder="Title" value={f.title} onChange={e=>setF({...f, title:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="archive-title"/>
        <div className="grid grid-cols-2 gap-3">
          <select value={f.category} onChange={e=>setF({...f, category:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid="archive-category">
            {CATEGORIES.filter(c=>c[0]!=="all").map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input type="number" placeholder="Year (optional)" value={f.year} onChange={e=>setF({...f, year:e.target.value})} className="px-3 py-2 font-mono text-sm" data-testid="archive-year"/>
        </div>
        <textarea rows={4} placeholder="Description" value={f.description} onChange={e=>setF({...f, description:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="archive-description"/>
        <input placeholder="Tags (comma-separated, e.g. treaty-9, water, elders)" value={f.tags} onChange={e=>setF({...f, tags:e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="archive-tags"/>
        <div>
          <label className="label-mono block mb-1">Attach file (PDF / image / audio, optional)</label>
          <input type="file" onChange={e=>setFile(e.target.files?.[0] || null)} className="font-mono text-xs text-white/70" data-testid="archive-file"/>
        </div>
        {err && <div className="text-[#ef5b6b] font-mono text-xs">{err}</div>}
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="submit-archive-btn">{saving?"Adding...":"Add to archive"}</button>
        </div>
      </form>
    </div>
  );
}
